<template>
  <div class="deliverables-field mt-3">
    <!-- No view permission message -->
    <div v-if="jobData?.job_id && !hasViewDeliverablesPermission" class="deliverables-no-permission">
      <v-icon large color="warning">mdi-lock-outline</v-icon>
      <span class="grey--text text--darken-1">{{ $t('no_permission_to_view_deliverables') }}</span>
    </div>

    <template v-else>
      <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

      <section v-if="baseInstances.length > 0 && !loading" class="deliverables-list">
        <div class="deliverables-list-header">
          <div class="deliverables-list-title">
            <v-icon color="grey darken-3" size="22">mdi-package-variant-closed</v-icon>
            <span>{{ $t('deliverables_list') }}</span>
          </div>

          <v-btn icon small class="deliverables-collapse-button" @click="toggleListCollapsed">
            <v-icon color="grey darken-3">
              {{ listCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
            </v-icon>
          </v-btn>
        </div>

        <div v-show="!listCollapsed" class="deliverables-table-wrapper">
          <v-simple-table dense class="deliverables-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="expand-column">
                    <v-btn
                      icon
                      small
                      class="row-expand-button deliverables-toggle-all-button"
                      :title="allBaseInstancesExpanded ? $t('collapse_all') : $t('expand_all')"
                      @click="toggleAllBaseInstances"
                    >
                      <v-icon>
                        {{ allBaseInstancesExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}
                      </v-icon>
                    </v-btn>
                  </th>
                  <th class="number-column">{{ $t('number_abbrev') || 'Nº' }}</th>
                  <th class="deliverable-column">{{ $t('deliverable') }}</th>
                  <th class="status-column">{{ $t('status') }}</th>
                  <th class="formats-count-column">{{ $t('formats') }}</th>
                  <th v-if="showPriceColumn">{{ $t('total') }}</th>
                </tr>
              </thead>

              <tbody>
                <template v-for="(baseInstance, index) in baseInstances">
                  <tr
                    :key="`base-${getBaseInstanceKey(baseInstance, index)}`"
                    class="deliverable-row"
                    @click="toggleBaseInstance(baseInstance, index)"
                  >
                    <td class="expand-column">
                      <v-btn icon small class="row-expand-button" @click.stop="toggleBaseInstance(baseInstance, index)">
                        <v-icon>
                          {{ isBaseExpanded(baseInstance, index) ? 'mdi-menu-down' : 'mdi-menu-right' }}
                        </v-icon>
                      </v-btn>
                    </td>
                    <td class="number-column">#{{ index + 1 }}</td>
                    <td class="deliverable-column">
                      <span class="deliverable-title">{{ baseInstance.base_title }}</span>
                    </td>
                    <td class="status-column">{{ getStatusTitle(baseInstance.status_id) }}</td>
                    <td class="formats-count-column">{{ getFormatInstanceCount(baseInstance) }}</td>
                    <td v-if="showPriceColumn">
                      {{ formatCurrency(getTotalPrice(baseInstance)) }}
                    </td>
                  </tr>

                  <tr
                    v-if="isBaseExpanded(baseInstance, index)"
                    :key="`formats-${getBaseInstanceKey(baseInstance, index)}`"
                    class="formats-row"
                  >
                    <td :colspan="showPriceColumn ? 6 : 5" class="formats-cell">
                      <div v-if="getFormatInstanceCount(baseInstance) > 0" class="formats-table-wrapper">
                        <v-simple-table dense class="formats-table">
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th></th>
                                <th>{{ $t('format') }}</th>
                                <th>{{ $t('title') }}</th>
                                <th v-if="showPriceColumn">{{ $t('total') }}</th>
                              </tr>
                            </thead>

                            <tbody>
                              <tr
                                v-for="(formatInstance, formatIndex) in baseInstance.formatInstances"
                                :key="getFormatInstanceKey(baseInstance, formatInstance, formatIndex)"
                              >
                                <td><v-icon left>mdi-file-image-outline</v-icon> {{ formatInstance.job_deliver.job_deliver_asset_count }}</td>
                                <td>{{ getFormatDescription(formatInstance) }}</td>
                                <td>{{ getFormatTitle(formatInstance) }}</td>
                                <td v-if="showPriceColumn">
                                  {{ formatCurrency(getFormatTotal(formatInstance)) }}
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>

                      <span v-else class="grey--text text--lighten-1">
                        {{ $t('no_formats_added') }}
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </section>

      <div v-else-if="!loading" class="deliverables-placeholder">
        <v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
        <span class="grey--text">{{ $t('no_deliverables_added') }}</span>
      </div>

      <div v-if="hasManageDeliverablesPermission" class="deliverables-actions">
        <v-btn color="primary" depressed @click="openDeliverableManagement">
          {{ $t('edit_deliverables').toLocaleUpperCase('pt-BR') }}
        </v-btn>

        <v-btn color="primary" outlined @click="openDeliverableAssets">
          {{ $t('approvals').toLocaleUpperCase('pt-BR') }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import JobDeliverableAPI from '@/services/api/job/deliverable'
import StatusAPI from '@/services/api/business/status'

export default {
  name: "DeliverablesField",

  props: {
    jobData: {
      type: Object,
      default: () => {}
    },

    readonly: {
      type: Boolean,
      default: false
    },

    field: {
      type: Object,
      default: () => {}
    },

    userAcls: {
      type: Array,
      default: () => []
    },

    lanes: {
      type: Array,
      default: () => []
    },

    // Pool configured in the job definition. During creation the job does
    // not have a lane yet, so this is the current pool used for the ACL check.
    currentPoolId: {
      type: [Number, String],
      default: null
    },

    // Pool that owns the job (comes from the job definition and cannot be
    // changed by the user). The create/edit deliverable ACL is associated
    // with this pool.
    ownerPoolId: {
      type: [Number, String],
      default: null
    },

    // Lanes the user can act on, either by being a lane executor or by
    // having the "act in all lanes" pool ACL.
    userLanes: {
      type: Array,
      default: () => []
    },

    appConfig: {
      type: Object,
      default: () => ({})
    },
  },

  data() {
    return {
      loading: false,
      baseInstances: [],
      statusOptions: [],
      showPriceColumn: false,
      expandedBaseInstances: {},
      listCollapsed: false,
    }
  },

  computed: {
    ...mapState('job', {
      tempDeliverables: state => state.tempDeliverables
    }),

    grandTotal() {
      return this.baseInstances.reduce((sum, baseInstance) => {
        return sum + this.getTotalPrice(baseInstance)
      }, 0)
    },

    appId() {
      return this.$route.params.appId || this.$store.state.app.selectedJobApp
    },

    hasManageDeliverablesPermission() {
      const EDIT_DELIVERABLES_ACL = 128
      const VIEW_DELIVERABLES_ACL = 256
      const ACT_IN_ALL_LANES_ACL = 2

      // A customer must be selected on the job before deliverables can be
      // managed.
      if (!this.jobData?.customer_id) {
        return false
      }

      // The form action is only available when the user can view or edit
      // deliverables in the job's current pool. During creation, the form
      // preselects an owner-pool lane, but the current pool is the one from
      // the job definition. Existing jobs use the pool of their current lane.
      const jobLane = this.lanes.find(l => l.lane_id == this.jobData?.lane_id)
      const lanePoolId = jobLane?.pool?.pool_id ?? jobLane?.pool_id
      const currentPoolId = this.jobData?.job_id
        ? lanePoolId
        : (this.currentPoolId ?? lanePoolId)

      const hasCurrentPoolDeliverablesAcl = currentPoolId && this.userAcls.some(acl =>
        acl.pool_id == currentPoolId &&
        ((acl.acl & EDIT_DELIVERABLES_ACL) !== 0 || (acl.acl & VIEW_DELIVERABLES_ACL) !== 0)
      )

      if (!hasCurrentPoolDeliverablesAcl) {
        return false
      }

      // The create/edit deliverable ACL is bound to the owner pool, which
      // comes from the job definition and cannot be changed by the user.
      const ownerPoolId = this.ownerPoolId

      if (!ownerPoolId) {
        return false
      }

      // 1) User must have the edit (create) deliverable ACL on the owner pool.
      const hasEditDeliverablesAcl = this.userAcls.some(acl =>
        acl.pool_id == ownerPoolId &&
        (acl.acl & EDIT_DELIVERABLES_ACL) !== 0
      )

      if (!hasEditDeliverablesAcl) {
        return false
      }

      // 2) On top of that, the user needs the "act" permission on the owner
      //    pool, either by having the "act in all lanes" pool ACL or by being
      //    an executor of one of the owner pool's lanes.
      const hasActInAllLanes = this.userAcls.some(acl =>
        acl.pool_id == ownerPoolId &&
        (acl.acl & ACT_IN_ALL_LANES_ACL) !== 0
      )

      if (hasActInAllLanes) {
        return true
      }

      const ownerPoolLaneIds = this.lanes
        .filter(l => (l.pool?.pool_id ?? l.pool_id) == ownerPoolId)
        .map(l => l.lane_id)

      const isExecutorInOwnerPool = ownerPoolLaneIds.some(laneId =>
        this.userLanes.includes(laneId)
      )

      return isExecutorInOwnerPool
    },

    allBaseInstancesExpanded() {
      if (this.baseInstances.length === 0) {
        return false
      }

      return this.baseInstances.every((baseInstance, index) => this.isBaseExpanded(baseInstance, index))
    },

    hasViewDeliverablesPermission() {
      const VIEW_DELIVERABLES_ACL = 256
      const EDIT_DELIVERABLES_ACL = 128

      if (!this.jobData?.lane_id) {
        return false
      }

      // Find the pool for this job's lane
      // NOTE: use loose equality (==) since lane_id/pool_id may come from
      // different sources (route params, API payloads) as either strings
      // or numbers, matching the comparisons used elsewhere (e.g.
      // JobTimelineView.jobCardActions/hasAclInPool and
      // JobListView.canManageDeliverables).
      const jobLane = this.lanes.find(l => l.lane_id == this.jobData.lane_id)
      const poolId = jobLane?.pool?.pool_id ?? jobLane?.pool_id

      if (!poolId) {
        return false
      }

      // Check if user has VIEW or EDIT permission (edit implies view)
      return this.userAcls.some(acl =>
        acl.pool_id == poolId &&
        ((acl.acl & VIEW_DELIVERABLES_ACL) !== 0 || (acl.acl & EDIT_DELIVERABLES_ACL) !== 0)
      )
    }
  },

  watch: {
    // Watch temp deliverables from store for job creation mode
    tempDeliverables: {
      deep: true,
      handler(newVal) {
        // Only use store deliverables when in creation mode (no job_id)
        if (this.jobData?.job_id) {
          return
        }

        if (newVal && newVal.length > 0) {
          this.loadDeliverablesFromStore(newVal)
        } else {
          this.setBaseInstances([], false)
        }
      }
    },

    'jobData.customer_id'(newVal) {
      if (!this.jobData?.job_id && newVal) {
        this.loadStatuses()
      }
    }
  },

  mounted() {
    this.loadDeliverables()
  },

  methods: {
    async loadDeliverables() {
      if (!this.jobData?.job_id) {
        // In creation mode, load from Vuex store if available
        if (this.tempDeliverables && this.tempDeliverables.length > 0) {
          await this.loadStatuses()
          this.loadDeliverablesFromStore(this.tempDeliverables)
        }
        return
      }

      // Check view permission before making backend calls
      if (!this.hasViewDeliverablesPermission) {
        console.log('User does not have permission to view deliverables')
        return
      }

      this.loading = true
      try {
        // Load statuses first
        await this.loadStatuses()

        // Fetch base instances with nested format_instances and billable_instances
        const response = await JobDeliverableAPI.getJobDeliverables(this.jobData.job_id)

        // Handle different response structures - response.data might be an object with items, or an array directly
        let deliverables = []
        if (Array.isArray(response.data)) {
          deliverables = response.data
        } else if (response.data && Array.isArray(response.data.items)) {
          deliverables = response.data.items
        } else if (response.data) {
          // Single object - wrap in array
          deliverables = [response.data]
        }

        // Check if any billable instance has a price field
        let hasPriceField = false

        // Transform the nested structure
        const baseInstances = deliverables
          .filter(item => {
            const hasBase = item && item.base_instance
            if (!hasBase) {
              console.warn('Item filtered out - missing base_instance:', item)
            }
            return hasBase
          })
          .map(item => {
            const baseInstance = { ...item.base_instance }

            // Map format instances with their nested billable instances
            baseInstance.formatInstances = (item.format_instances || []).filter(Boolean).map(formatItem => {
              const formatInstance = this.normalizeFormatInstance(formatItem.format_instance || formatItem)

              // Map billable instances
              formatInstance.billableInstances = (formatItem.billable_instances || []).map(billableItem => {
                // Check if this billable has a price field
                if (billableItem.price !== undefined && billableItem.price !== null) {
                  hasPriceField = true
                }

                return {
                  ...billableItem,
                  price: billableItem.price !== undefined ? billableItem.price : 0
                }
              })

              formatInstance.job_deliver = formatItem.job_deliver || {}

              return formatInstance
            })

            return baseInstance
          })

        this.setBaseInstances(baseInstances, hasPriceField)

      } catch (error) {
        console.error('Error loading deliverables:', error)
        this.setBaseInstances([], false)
      } finally {
        this.loading = false
      }
    },

    async loadStatuses() {
      if (!this.jobData?.customer_id) return
      try {
        const response = await StatusAPI.getAllStatusesByCustomer(this.jobData.customer_id, {
          order: 'status_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.statusOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading statuses:', error)
      }
    },

    getStatusTitle(statusId) {
      const status = this.statusOptions.find(s => s.status_id == statusId)
      return status ? status.status_title : this.$t('draft')
    },

    getFormatInstanceCount(baseInstance) {
      return baseInstance.formatInstances?.length || 0
    },

    normalizeFormatInstance(formatInstance = {}) {
      return {
        ...formatInstance,
        format_desc: formatInstance.format_desc ?? formatInstance.format ?? ''
      }
    },

    getFormatDescription(formatInstance) {
      return formatInstance.format_desc || formatInstance.format || '-'
    },

    getFormatTitle(formatInstance) {
      return formatInstance.format_title || this.getFormatDescription(formatInstance)
    },

    getFormatInstanceKey(baseInstance, formatInstance, formatIndex) {
      return formatInstance.format_instance_id ||
        `${baseInstance.base_instance_id || baseInstance.base_title}-${formatIndex}-${this.getFormatDescription(formatInstance)}`
    },

    getFormatQuantity(formatInstance) {
      return Number(formatInstance.quantity) || 1
    },

    getTotalPrice(baseInstance) {
      if (!baseInstance.formatInstances) return 0

      return baseInstance.formatInstances.reduce((baseSum, formatInstance) => {
        if (!formatInstance.billableInstances) return baseSum

        const formatTotal = formatInstance.billableInstances.reduce((formatSum, billable) => {
          const quantity = Number(billable.quantity) || 0
          const price = Number(billable.price) || 0
          return formatSum + (quantity * price)
        }, 0)

        return baseSum + formatTotal
      }, 0)
    },

    getFormatTotal(formatInstance) {
      if (!formatInstance.billableInstances) return 0

      return formatInstance.billableInstances.reduce((formatSum, billable) => {
        const quantity = Number(billable.quantity) || 0
        const price = Number(billable.price) || 0
        return formatSum + (quantity * price)
      }, 0)
    },

    getBaseInstanceKey(baseInstance, index) {
      return baseInstance.base_instance_id || `${baseInstance.base_title || 'deliverable'}-${index}`
    },

    isBaseExpanded(baseInstance, index) {
      return Boolean(this.expandedBaseInstances[this.getBaseInstanceKey(baseInstance, index)])
    },

    toggleBaseInstance(baseInstance, index) {
      const key = this.getBaseInstanceKey(baseInstance, index)
      this.$set(this.expandedBaseInstances, key, !this.expandedBaseInstances[key])
    },

    toggleListCollapsed() {
      this.listCollapsed = !this.listCollapsed
    },

    toggleAllBaseInstances() {
      const shouldExpand = !this.allBaseInstancesExpanded
      const expandedBaseInstances = {}

      if (shouldExpand) {
        this.baseInstances.forEach((baseInstance, index) => {
          expandedBaseInstances[this.getBaseInstanceKey(baseInstance, index)] = true
        })
      }

      this.expandedBaseInstances = expandedBaseInstances
    },

    setBaseInstances(baseInstances, hasPriceField) {
      this.baseInstances = baseInstances
      this.showPriceColumn = hasPriceField
      this.expandFirstBaseInstance()
    },

    expandFirstBaseInstance() {
      this.expandedBaseInstances = {}

      if (this.baseInstances.length === 0) {
        return
      }

      const firstBaseInstance = this.baseInstances[0]
      this.$set(this.expandedBaseInstances, this.getBaseInstanceKey(firstBaseInstance, 0), true)
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0)
    },

    openDeliverableManagement() {
      // If job has an ID (editing mode), open in new tab
      if (this.jobData?.job_id) {
        const route = this.$router.resolve({
          name: 'JobDeliverableManagement',
          params: {
            appId: this.$route.params.appId,
            jobId: this.jobData.job_id
          }
        })
        window.open(route.href, '_blank')
      } else {
        // If creating new job, emit event to parent to open deliverables panel
        this.$emit('openDeliverablesPanel')
      }
    },

    openDeliverableAssets() {
      // If job has an ID (editing mode), open in new tab
      if (this.jobData?.job_id) {
        const contentAppId = this.appConfig?.content_app_inst_id || this.appConfig?.content_app_id
        const businessAppId = this.appConfig?.business_app_inst_id || this.appConfig?.business_app_id
        const route = this.$router.resolve({
          name: 'JobDeliverableAssets',
          params: {
            appId: this.$route.params.appId,
            jobId: this.jobData.job_id,
            contentAppId,
            businessAppId
          }
        })
        window.open(route.href, '_blank')
      } else {
        // If creating new job, emit event to parent to open deliverables panel
        this.$emit('openDeliverablesPanel')
      }
    },

    loadDeliverablesFromStore(storeData) {
      if (!storeData || !Array.isArray(storeData)) {
        this.setBaseInstances([], false)
        return
      }

      // Check if any billable instance has a price field
      let hasPriceField = false

      // Transform the store data to match the component's structure
      const baseInstances = storeData
        .map(item => {
          const baseInstance = { ...(item.base_instance || item) }

          // Map format instances
          baseInstance.formatInstances = (item.format_instances || []).filter(Boolean).map(formatItem => {
            const formatInstance = this.normalizeFormatInstance(formatItem.format_instance || formatItem)
            // Map billable instances
            formatInstance.billableInstances = (formatItem.billable_instances || []).map(billableItem => {
              // Check if this billable has a price field
              if (billableItem.price !== undefined && billableItem.price !== null) {
                hasPriceField = true
              }

              return {
                ...billableItem,
                price: billableItem.price !== undefined ? billableItem.price : 0
              }
            })

            formatInstance.job_deliver = formatItem.job_deliver || {}

            return formatInstance
          })

          return baseInstance
        })

      this.setBaseInstances(baseInstances, hasPriceField)
    },

    // This method is called by the parent component after job submission
    async postSubmitAction() {
      // Refresh the deliverables data after save
      await this.loadDeliverables()
    },
  },
}
</script>

<style scoped>
.deliverables-field {
  border-top: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
  padding: 24px 0;
}

.deliverables-list {
  width: 100%;
}

.deliverables-list-header {
  align-items: center;
  border-left: 2px solid #00c853;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  min-height: 30px;
  padding-left: 10px;
}

.deliverables-list-title {
  align-items: center;
  color: #111;
  display: flex;
  font-size: 18px;
  font-weight: 700;
  gap: 8px;
  line-height: 1.2;
}

.deliverables-collapse-button {
  margin-right: -8px;
}

.deliverables-toggle-all-button {
  margin-left: -8px;
}

.deliverables-table-wrapper {
  border: 1px solid #d6d6d6;
  border-radius: 4px;
  overflow: hidden;
}

.deliverables-table ::v-deep table,
.formats-table ::v-deep table {
  border-collapse: collapse;
}

.deliverables-table ::v-deep th {
  border-bottom: 1px solid #d6d6d6;
  color: #666;
  font-size: 12px;
  font-weight: 400;
  height: 28px;
  padding: 0 12px;
  white-space: nowrap;
}

.deliverables-table ::v-deep td {
  border-bottom: 1px solid #d6d6d6;
  color: #111;
  font-size: 14px;
  height: 38px;
  padding: 0 12px;
  vertical-align: middle;
}

.deliverables-table ::v-deep tbody > tr:last-child > td {
  border-bottom: 0;
}

.expand-column {
  text-align: center;
  width: 34px;
}

.number-column {
  text-align: left;
  width: 90px;
}

.deliverable-column {
  text-align: left;
}

.status-column {
  text-align: left;
  width: 150px;
}

.formats-count-column {
  text-align: left;
  width: 120px;
}

.total-column,
.nested-total-column {
  text-align: right;
  width: 140px;
}

.deliverable-row {
  background-color: #fff;
  cursor: pointer;
}

.deliverable-row:hover {
  background-color: #fafafa;
}

.deliverable-title {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-expand-button {
  color: #505050;
  margin-left: -8px;
}

.formats-row {
  background-color: #eeeeee;
}

.formats-row ::v-deep td {
  border-bottom: 1px solid #d6d6d6;
}

.formats-cell {
  padding: 16px !important;
}

.formats-table-wrapper {
  background-color: #fff;
  border: 1px solid #d6d6d6;
  border-radius: 4px;
  overflow: hidden;
}

.formats-table ::v-deep th {
  border-bottom: 1px solid #d6d6d6;
  color: #666;
  font-size: 12px;
  font-weight: 400;
  height: 28px;
  padding: 0 18px;
  white-space: nowrap;
}

.formats-table ::v-deep td {
  border-bottom: 1px solid #d6d6d6;
  color: #111;
  font-size: 14px;
  height: 38px;
  padding: 0 18px;
}

.formats-table ::v-deep tbody > tr:last-child > td {
  border-bottom: 0;
}

.deliverables-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.deliverables-approvals-button {
  border-color: #00c853 !important;
  color: #00a844 !important;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
}

.deliverables-approvals-button.v-btn--disabled {
  border-color: #00c853 !important;
  color: #00a844 !important;
  opacity: .55;
}

.deliverables-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  padding: 30px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
}

.deliverables-placeholder .v-icon {
  margin-bottom: 8px;
}

.deliverables-no-permission {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  padding: 30px;
  border: 2px solid #fb8c00;
  border-radius: 8px;
  background-color: #fff3e0;
}

.deliverables-no-permission .v-icon {
  margin-bottom: 8px;
}
</style>
